// utils/sessions.ts
import { dayKey } from "../utils/date"; // uses DISPLAY_TZ + DISPLAY_LOCALE
import type { Session } from "../types";
import { isSessionWithTimes } from "../types";

export type SessionWithTimes = Required<Pick<Session, "time_start" | "time_end">> & Session;

export type SelectionResult =
  | { kind: "TODAY"; sessions: SessionWithTimes[] }
  | { kind: "TOMORROW"; sessions: SessionWithTimes[] }
  | { kind: "NONE" };

export interface SelectionOptions {
  /** For dev/testing: pretend “now” is this date */
  baseDate?: Date;
  /** If we selected "today", hide sessions that have already finished */
  showUnfinishedOnlyToday?: boolean;
}

/** Normalize: ensure times exist and sort ascending by start */
const normalizeSessions = (raw: Session[]): SessionWithTimes[] =>
  raw
    .filter(isSessionWithTimes)
    .sort((a, b) => (a.time_start as number) - (b.time_start as number)) as SessionWithTimes[];

/** Compute a YYYY-MM-DD key for a Date via dayKey() (display TZ/locale) */
const keyFromDate = (d: Date) => dayKey(Math.floor(d.getTime() / 1000));

export function selectSessionsForBoard(
  raw: Session[],
  { baseDate, showUnfinishedOnlyToday = true }: SelectionOptions = {}
): SelectionResult {
  const now = baseDate ?? new Date();
  const nowEpoch = Math.floor(now.getTime() / 1000);

  const sessions = normalizeSessions(raw);

  // Group sessions by display-day using dayKey()
  const byDay = new Map<string, SessionWithTimes[]>();
  for (const s of sessions) {
    const k = dayKey(s.time_start!);
    (byDay.get(k) ?? byDay.set(k, []).get(k)!).push(s as SessionWithTimes);
  }

  // Today/Tomorrow in display TZ using dayKey()
  const todayKey = keyFromDate(now);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowKey = keyFromDate(tomorrow);

  // 1) Try today
  let todays = byDay.get(todayKey) ?? [];
  if (todays.length) {
    if (showUnfinishedOnlyToday) {
      todays = todays.filter(s => s.time_end! > nowEpoch);
    }
    if (todays.length) {
      return { kind: "TODAY", sessions: todays };
    }
    // If all today's sessions are finished and filtered out, fall through to tomorrow.
  }

  // 2) Try tomorrow
  const tomorrows = byDay.get(tomorrowKey) ?? [];
  if (tomorrows.length) return { kind: "TOMORROW", sessions: tomorrows };

  // 3) Nothing to show
  return { kind: "NONE" };
}
